import React, { useEffect, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { formatNum } from "../../lib/utils";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { StatusChip, STATUS_OPTS, TIER_OPTS } from "./shared";

export function Scope3Tab({ d, canEdit, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const m = {};
    (d?.categories || []).forEach((c) => { m[c.cat] = { status: c.status, dq_tier: c.dq_tier || "C" }; });
    setDraft(m);
  }, [d]);
  if (!d) return <div className="card-flat p-6 text-sm text-muted-foreground" data-testid="ghg-scope3-loading">Loading Scope 3…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const items = (d.categories || []).map((c) => ({ cat: c.cat, status: draft[c.cat]?.status, dq_tier: draft[c.cat]?.dq_tier }));
      await api.put("/ghg/scope3/screen", { items });
      toast.success("Scope 3 screen saved"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  const ss = d.screening_status;
  const ssTxt = ss === "screened" ? { l: "Screening complete", c: "text-[#00E65B]" } : ss === "in_progress" ? { l: "In progress", c: "text-[#FFB800]" } : { l: "Not yet screened", c: "text-[#FFB800]" };
  const groups = ["Upstream", "Downstream"];

  return (
    <div className="space-y-4" data-testid="ghg-scope3-tab">
      <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-3" data-testid="ghg-scope3-header">
        <div>
          <div className="label-eyebrow text-[10px]">Total Scope 3 · FY{d.year}</div>
          <div className="font-mono text-3xl font-bold" data-testid="ghg-scope3-total">{formatNum(d.total_scope3_tonnes, { decimals: 2 })} <span className="text-sm text-muted-foreground">tCO₂e</span></div>
          <div className="text-[11px] text-muted-foreground mt-1">{d.active_count} active · {d.screened_count} screened — not material · all 15 categories shown</div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`label-eyebrow text-[10px] ${ssTxt.c}`} data-testid="ghg-scope3-status">{ssTxt.l}</span>
          {canEdit && <Button variant="primary" onClick={save} disabled={saving} data-testid="ghg-scope3-save">{saving ? "Saving…" : "Save screen"}</Button>}
        </div>
      </div>

      {groups.map((g) => (
        <div key={g} className="card-flat p-4" data-testid={`ghg-scope3-group-${g.toLowerCase()}`}>
          <div className="label-eyebrow text-[10px] mb-3">{g}</div>
          <div className="space-y-2">
            {(d.categories || []).filter((c) => c.group === g).map((c) => (
              <div key={c.cat} className="flex items-center justify-between gap-3 py-1.5 border-b border-border last:border-0" data-testid={`ghg-scope3-cat-${c.cat}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium flex items-center gap-2">
                    <span className="font-mono text-muted-foreground">Cat {c.cat}</span> {c.name}
                    {c.status === "active" && c.dq_tier && (
                      <span className="inline-flex items-center rounded-full bg-accent/15 text-accent border border-accent/30 px-2 py-0.5 text-[9px] font-medium" data-testid={`ghg-scope3-tier-${c.cat}`}>
                        DQ {c.dq_tier} · {c.dq_label}
                      </span>
                    )}
                  </div>
                  {c.status === "active" ? (
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5" data-testid={`ghg-scope3-fig-${c.cat}`}>
                      {formatNum(c.co2e_tonnes, { decimals: 2 })} tCO₂e · {c.share_pct}% · {c.records} rec
                    </div>
                  ) : (
                    <div className="text-[10px] text-muted-foreground mt-0.5">{c.note || c.typical_method}</div>
                  )}
                  {c.catalog_entries?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`ghg-scope3-subs-${c.cat}`}>
                      {c.catalog_entries.map((e) => (
                        <span key={e.code} className="inline-flex items-center rounded-full bg-surface-hover border border-border px-1.5 py-0.5 text-[9px] text-muted-foreground" data-testid={`ghg-scope3-sub-${e.code}`}>
                          {e.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canEdit && draft[c.cat]?.status === "active" && (
                    <Select value={draft[c.cat]?.dq_tier || "C"} onValueChange={(v) => setDraft((p) => ({ ...p, [c.cat]: { ...p[c.cat], dq_tier: v } }))}>
                      <SelectTrigger className="w-16 h-8" data-testid={`ghg-scope3-tier-select-${c.cat}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TIER_OPTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {canEdit ? (
                    <Select value={draft[c.cat]?.status || "not_yet_screened"} onValueChange={(v) => setDraft((p) => ({ ...p, [c.cat]: { ...p[c.cat], status: v } }))}>
                      <SelectTrigger className="w-52" data-testid={`ghg-scope3-select-${c.cat}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <StatusChip status={c.status} testid={`ghg-scope3-chip-${c.cat}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
